/**
 * The end of a run — *"death is the product's most memorable moment, and it
 * spends it on a flag that hides the input box."*
 *
 * Four things have to be true at once here, and the first one decides the shape of the rest:
 *
 * 1. **The prose lands first.** The `death` event arrives *before* `chapter`, so the client
 * knows the character is dead while the last paragraph is still being written a token at a
 * time. The anti-pattern the peripheral surfaces names is a modal that interrupts it. So nothing in this file
 * watches the `death` event at all — `PlayRoute` renders this only once the turn has
 * committed, which is the moment the narration is complete by definition, and the reveal
 * itself is a delayed CSS animation rather than a timer.
 * 2. **The epitaph is the game's best joke and is presented as one.** It is a whole sentence
 * the DM wrote (`claude-haiku-4-5` writes it inline at the end of a fatal turn), and it is
 * the only thing in this app outside the feed set in the narration serif — because it is
 * the world speaking, not the interface.
 * 3. **The numbers come after the sentence and before the way out.** That order is the whole
 * of the sequence: the world speaks, then the run is totted up, then — and only then — the
 * player is offered another one. Reversing the last two would be a *play again* button
 * shouting over an epitaph, which is the mistake every one of the delays below exists to
 * avoid. `runStats.ts` argues what the four numbers are and why two of them are optional.
 * 4. **The story is read-only, and that is expressed by absence.** `PlayRoute` renders no
 * `ActionBar` on a dead story rather than a disabled one: a greyed-out box is a promise
 * that something is coming back, and nothing is. The way out is here instead.
 *
 * **The feed behind this is dimmed and desaturated, and that is not done here** — it is a
 * class `StoryFeed` puts on the scrollback, because the treatment belongs to the chapters and
 * this card must stay at full strength in front of them. See `.feed-spent` in `index.css`.
 *
 * **What is still deliberately not here**: the shareable run card and the leaderboard
 * placement (`TODO.md` §7.2, §7.4). Both are additive to this card and neither changes its
 * shape, which is why deferring them stays safe rather than merely convenient.
 */
import { Link } from 'react-router';
import type { RunStat } from '@/features/story/runStats';
import { useT } from '@/i18n/context';

interface DeathBeatProps {
  characterName: string;
  /** `cause_of_death` — the DM's one-liner. Never null on a dead story; the death beat owns it. */
  epitaph: string;
  /** What the run came to. Two to four rows — see `runStats.ts`. */
  stats: readonly RunStat[];
}

export function DeathBeat({ characterName, epitaph, stats }: DeathBeatProps) {
  const t = useT();

  return (
    <section
      aria-label={t('ui.death.section_label')}
      className="mt-[var(--space-chapter)] flex flex-col items-center gap-6 text-center motion-safe:animate-[death-reveal_900ms_ease-out_both]"
    >
      <span aria-hidden="true" className="bg-rule h-px w-16" />

      <p
        className="text-fg-muted font-mono tracking-wider"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {t('ui.death.is_dead', { name: characterName })}
      </p>

      {/*
        The first delay in the sequence, and the whole of "present it deliberately": the rule and
        the name arrive, and the sentence that is the point of the moment arrives after them.
        `both` holds the from-state through the delay so it fades *in* rather than flashing and
        then animating; reduced motion collapses the whole ladder to nothing and the card is
        simply there, which is the correct reading of that preference rather than a degraded one.
      */}
      <p
        className="text-fg motion-safe:animate-[death-reveal_900ms_ease-out_600ms_both]"
        style={{
          fontFamily: 'var(--font-narration)',
          fontSize: 'var(--text-narration)',
          lineHeight: 'var(--leading-narration)',
          maxWidth: '28rem',
        }}
      >
        {epitaph}
      </p>

      {/*
        A `<dl>` because that is what it is — four labelled figures, not a table and not a
        sentence. Pairing them in the markup is what lets a screen reader read "chapters,
        twelve" instead of two disconnected runs of words, and it is why the label and the
        number are one grid cell each rather than two columns that happen to line up.

        `tabular-nums` so the column of figures is a column: proportional digits in a mono
        stack still drift on the few faces that have them, and this is the one place in the app
        where numbers are read against each other rather than in a sentence.
      */}
      <dl
        className="text-fg-muted grid grid-cols-[auto_auto] gap-x-6 gap-y-1 text-left font-mono motion-safe:animate-[death-reveal_900ms_ease-out_1100ms_both]"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {stats.map((stat) => (
          <div key={stat.key} className="col-span-2 grid grid-cols-subgrid">
            <dt className="text-fg-faint">{t(stat.key)}</dt>
            <dd className="text-fg justify-self-end tabular-nums">{stat.value}</dd>
          </div>
        ))}
      </dl>

      <div className="flex flex-wrap justify-center gap-3 motion-safe:animate-[death-reveal_900ms_ease-out_1600ms_both]">
        <Link
          to="/stories/new"
          className="border-accent text-accent hover:bg-accent hover:text-bg border px-4 py-2 font-mono no-underline transition-colors"
          style={{ fontSize: 'var(--text-action)' }}
        >
          {t('ui.death.new_story')}
        </Link>
        <Link
          to="/stories"
          className="border-border text-fg-muted hover:border-accent hover:text-accent border px-4 py-2 font-mono no-underline transition-colors"
          style={{ fontSize: 'var(--text-action)' }}
        >
          {t('ui.nav.your_stories')}
        </Link>
      </div>
    </section>
  );
}
